import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Square, RotateCcw } from 'lucide-react';

interface AgentInfo {
  name: string;
  type: 'fast' | 'reasoning';
  status: 'running' | 'idle' | 'error' | 'stopped';
  lastHeartbeat: string;
  memoryUsage: number;
  uptime: string;
  tasksCompleted: number;
}

const AgentsPage: React.FC = () => {
  const [agents, setAgents] = useState<AgentInfo[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const response = await fetch('/api/admin/agents');
        if (response.ok) {
          const data = await response.json();
          setAgents(data);
        } else {
          setAgents([
            {
              name: 'beehive-fast-01',
              type: 'fast',
              status: 'running',
              lastHeartbeat: new Date().toISOString(),
              memoryUsage: 512,
              uptime: '2h 34m',
              tasksCompleted: 45
            },
            {
              name: 'beehive-reasoning-01',
              type: 'reasoning',
              status: 'running',
              lastHeartbeat: new Date().toISOString(),
              memoryUsage: 1024,
              uptime: '1h 12m',
              tasksCompleted: 12
            },
            {
              name: 'beehive-fast-02',
              type: 'fast',
              status: 'idle',
              lastHeartbeat: new Date(Date.now() - 30000).toISOString(),
              memoryUsage: 256,
              uptime: '45m',
              tasksCompleted: 23
            }
          ]);
        }
      } catch (error) {
        console.error('Failed to fetch agents:', error);
        setAgents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
    const interval = setInterval(fetchAgents, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedAgent) {
      const fetchLogs = async () => {
        try {
          const response = await fetch(`/api/admin/logs?agent=${selectedAgent}`);
          if (response.ok) {
            const data = await response.json();
            setLogs(data.logs || []);
          } else {
            setLogs([
              `[${new Date().toISOString()}] Agent ${selectedAgent} initialized`,
              `[${new Date().toISOString()}] Processing task queue...`,
              `[${new Date().toISOString()}] Task completed successfully`,
              `[${new Date().toISOString()}] Waiting for next task...`
            ]);
          }
        } catch (error) {
          console.error('Failed to fetch logs:', error);
          setLogs([`Error fetching logs for ${selectedAgent}`]);
        }
      };

      fetchLogs();
      const interval = setInterval(fetchLogs, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedAgent]);

  const handleAgentAction = async (agentName: string, action: 'restart' | 'shutdown') => {
    try {
      const response = await fetch(`/api/admin/agents/${agentName}/${action}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        setAgents(prev => prev.map(agent => 
          agent.name === agentName 
            ? { ...agent, status: action === 'restart' ? 'running' : 'stopped' }
            : agent
        ));
      } else {
        console.error(`Failed to ${action} agent ${agentName}`);
      }
    } catch (error) {
      console.error(`Error ${action}ing agent:`, error);
    }
  };

  const getStatusColor = (status: AgentInfo['status']) => {
    switch (status) {
      case 'running': return 'text-green-400 bg-green-500/20';
      case 'idle': return 'text-yellow-400 bg-yellow-500/20';
      case 'error': return 'text-red-400 bg-red-500/20';
      case 'stopped': return 'text-slate-400 bg-slate-500/20';
      default: return 'text-slate-400 bg-slate-500/20';
    }
  };

  const getTypeColor = (type: AgentInfo['type']) => {
    return type === 'fast' ? 'text-blue-400 bg-blue-500/20' : 'text-purple-400 bg-purple-500/20';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white text-drop-shadow">Agent Management</h1>
        <p className="text-slate-400">Monitor and control Beehive agents</p>
      </div>

      <div className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border-b border-slate-700/30">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Agent</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Type</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Status</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Last Heartbeat</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Memory</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Uptime</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Tasks</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent, index) => (
                <motion.tr
                  key={agent.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border-b border-slate-700/30 hover:bg-slate-800/30 cursor-pointer"
                  onClick={() => setSelectedAgent(agent.name)}
                >
                  <td className="p-4">
                    <div className="font-medium text-white">{agent.name}</div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(agent.type)}`}>
                      {agent.type}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(agent.status)}`}>
                      {agent.status}
                    </span>
                  </td>
                  <td className="p-4 text-slate-300 text-sm">
                    {new Date(agent.lastHeartbeat).toLocaleTimeString()}
                  </td>
                  <td className="p-4 text-slate-300 text-sm">
                    {agent.memoryUsage}MB
                  </td>
                  <td className="p-4 text-slate-300 text-sm">
                    {agent.uptime}
                  </td>
                  <td className="p-4 text-slate-300 text-sm">
                    {agent.tasksCompleted}
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAgentAction(agent.name, 'restart');
                        }}
                        className="p-1 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded"
                        title="Restart"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAgentAction(agent.name, 'shutdown');
                        }}
                        className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded"
                        title="Shutdown"
                      >
                        <Square className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedAgent && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-lg"
        >
          <div className="p-4 border-b border-slate-700/50">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                Logs: {selectedAgent}
              </h3>
              <button
                onClick={() => setSelectedAgent(null)}
                className="text-slate-400 hover:text-white"
              >
                ×
              </button>
            </div>
          </div>
          <div className="p-4">
            <div className="bg-slate-900/50 rounded-lg p-4 h-64 overflow-y-auto font-mono text-sm">
              {logs.map((log, index) => (
                <div key={index} className="text-slate-300 mb-1">
                  {log}
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AgentsPage;
